import type { IntervalName } from './notes';

export type QualitySlug =
  | 'maj' | 'm' | '5' | '7' | 'maj7' | 'm7' | 'm7b5' | 'sus2'
  | 'sus4' | 'dim' | 'dim7' | 'aug' | '6' | 'm6' | 'add9' | '9';

export type ChordType = 'major' | 'minor' | 'seventh' | 'sus' | 'power' | 'other';

export interface ChordQuality {
  slug: QualitySlug;
  /** Symbol suffix shown after the root (`maj` has none). */
  suffix: string;
  intervals: readonly IntervalName[];
  /** Persian quality name, appended to the Persian root name. */
  nameFa: string;
  /** English name with a `{r}` placeholder for the root display. */
  nameEn: string;
  type: ChordType;
  /** Other accepted suffix spellings for search and parsing (the `suffix` itself is implied). */
  aliases: readonly string[];
}

/** Table order is the canonical order (AC-02). */
export const QUALITIES: readonly ChordQuality[] = [
  { slug: 'maj', suffix: '', intervals: ['1', '3', '5'], nameFa: 'ماژور', nameEn: '{r} major', type: 'major', aliases: ['maj', 'major', 'M'] },
  { slug: 'm', suffix: 'm', intervals: ['1', 'b3', '5'], nameFa: 'مینور', nameEn: '{r} minor', type: 'minor', aliases: ['min', '-', 'minor'] },
  { slug: '5', suffix: '5', intervals: ['1', '5'], nameFa: 'پاورکورد', nameEn: '{r}5 (power chord)', type: 'power', aliases: ['power'] },
  { slug: '7', suffix: '7', intervals: ['1', '3', '5', 'b7'], nameFa: 'هفت', nameEn: '{r} dominant 7th', type: 'seventh', aliases: [] },
  { slug: 'maj7', suffix: 'maj7', intervals: ['1', '3', '5', '7'], nameFa: 'ماژور هفت', nameEn: '{r} major 7th', type: 'seventh', aliases: ['M7', 'Δ7', 'Δ'] },
  { slug: 'm7', suffix: 'm7', intervals: ['1', 'b3', '5', 'b7'], nameFa: 'مینور هفت', nameEn: '{r} minor 7th', type: 'seventh', aliases: ['min7', '-7'] },
  { slug: 'm7b5', suffix: 'm7b5', intervals: ['1', 'b3', 'b5', 'b7'], nameFa: 'نیم‌کاسته', nameEn: '{r} half-diminished', type: 'seventh', aliases: ['ø', 'ø7', 'min7b5', '-7b5'] },
  { slug: 'sus2', suffix: 'sus2', intervals: ['1', '2', '5'], nameFa: 'ساس ۲', nameEn: '{r} suspended 2nd', type: 'sus', aliases: [] },
  { slug: 'sus4', suffix: 'sus4', intervals: ['1', '4', '5'], nameFa: 'ساس ۴', nameEn: '{r} suspended 4th', type: 'sus', aliases: ['sus'] },
  { slug: 'dim', suffix: 'dim', intervals: ['1', 'b3', 'b5'], nameFa: 'کاسته', nameEn: '{r} diminished', type: 'other', aliases: ['°'] },
  { slug: 'dim7', suffix: 'dim7', intervals: ['1', 'b3', 'b5', 'bb7'], nameFa: 'کاسته هفت', nameEn: '{r} diminished 7th', type: 'other', aliases: ['°7'] },
  { slug: 'aug', suffix: 'aug', intervals: ['1', '3', '#5'], nameFa: 'افزوده', nameEn: '{r} augmented', type: 'other', aliases: ['+'] },
  { slug: '6', suffix: '6', intervals: ['1', '3', '5', '6'], nameFa: 'شش', nameEn: '{r} 6th', type: 'other', aliases: [] },
  { slug: 'm6', suffix: 'm6', intervals: ['1', 'b3', '5', '6'], nameFa: 'مینور شش', nameEn: '{r} minor 6th', type: 'other', aliases: ['min6'] },
  { slug: 'add9', suffix: 'add9', intervals: ['1', '3', '5', '9'], nameFa: 'اَد ۹', nameEn: '{r} add 9', type: 'other', aliases: [] },
  { slug: '9', suffix: '9', intervals: ['1', '3', '5', 'b7', '9'], nameFa: 'نُه', nameEn: '{r} dominant 9th', type: 'other', aliases: [] }
];

const BY_SLUG = new Map<string, ChordQuality>(QUALITIES.map((q) => [q.slug, q]));

export const isQualitySlug = (s: string): s is QualitySlug => BY_SLUG.has(s);
export const getQuality = (slug: QualitySlug): ChordQuality => BY_SLUG.get(slug)!;

/** URL values and Persian labels of the type filter (AC-04). */
export const CHORD_TYPES: ReadonlyArray<{ value: ChordType; fa: string }> = [
  { value: 'major', fa: 'ماژور' },
  { value: 'minor', fa: 'مینور' },
  { value: 'seventh', fa: 'هفت' },
  { value: 'sus', fa: 'ساس' },
  { value: 'power', fa: 'پاورکورد' },
  { value: 'other', fa: 'سایر' }
];

export const isChordType = (s: string): s is ChordType => CHORD_TYPES.some((t) => t.value === s);
